/**
 * @category LeftSide
 * @component
 * @module Files/StickyFileWindow
 * @description This component is a sticky window that pops up when we want to give user an ability to edit a file.
 * At the moment it can be called from the graph component and from the file uploader component.
 */
import React, { useState, useEffect, useContext } from "react";
import { MenuItem, Select, FormControl } from "@mui/material";
import { deepCopy } from "../Models/modelLogic";
import {
  filterCurveDataToRange,
  isDeepEqual,
  revertToOriginalPoints,
  segmentsIntoSingleCurve,
  splitCurveIntoSegmentsByEdges,
  getShortName,
  updateFileDataAfterOptionChange,
  isInverseUnit,
  cm1ToAnyUnit,
  anyUnitToCm1,
} from "../../../utils/helpers";
import Range from "./Range";
import units from "../../../utils/units";
import "../leftSide.scss";
import addIcon from "../../../res/icons/add.png";
import deleteIcon from "../../../res/icons/delete.png";
// import close from "../../../res/icons/delete.png";
// import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  dataPointsUpdateWithOptions,
  revertDataPointsToOriginalUnits,
} from "./fileProcessing/processingLogic";
import { DashboardContext } from "../../../context/DashboardContext";
import { GeneralContext } from "../../../context/GeneralContext";
import CloseButton from "../../commonComponents/CloseButton";
import HtmlTooltip from "../../commonComponents/HtmlTooltip";
import CustomButton from "../../commonComponents/CustomButton";

// Store displayRangeUnit outside component to persist across file switches
let persistedDisplayRangeUnit = null;

function StickyFileWindow(params) {
  const { selectedFiles, updateSelected, callFromGraph, resetSelection } =
    params;
  const { setFileToChange, uploadedFiles, defaultXAxisUnit } =
    useContext(DashboardContext);
  const { recordedErrorLog } = useContext(GeneralContext);

  // Get the index from selectedFiles (static), but always get the current file from uploadedFiles
  const index = selectedFiles[0].index;
  const file = uploadedFiles[index];

  const [xUnit, setXUnit] = useState(file.xUnit);
  const [rangeMin, setRangeMin] = useState(file.dataRangeMin);
  const [rangeMax, setRangeMax] = useState(file.dataRangeMax);
  const [edges, setEdges] = useState(file.edges);
  // const [npoints, setNpoints] = useState(file.npoints);
  const [pointsInRange, setPointsInRange] = useState("N/A");
  // Default to defaultXAxisUnit if it's not an inverse unit, otherwise fall back to units[0]
  const getDefaultDisplayRangeUnit = () => {
    if (defaultXAxisUnit && !isInverseUnit(defaultXAxisUnit)) {
      return defaultXAxisUnit;
    }
    return units[0];
  };
  // Use persisted value if available, otherwise initialize with default
  const [displayRangeUnit, setDisplayRangeUnit] = useState(() => {
    if (persistedDisplayRangeUnit) {
      return persistedDisplayRangeUnit;
    }
    return getDefaultDisplayRangeUnit();
  });

  const [displayRangeMin, setDisplayRangeMin] = useState(
    cm1ToAnyUnit(displayRangeUnit, file.dataRangeMin)
  );
  const [displayRangeMax, setDisplayRangeMax] = useState(
    cm1ToAnyUnit(displayRangeUnit, file.dataRangeMax)
  );
  // Update persisted value whenever displayRangeUnit changes (user selection)
  useEffect(() => {
    persistedDisplayRangeUnit = displayRangeUnit;
  }, [displayRangeUnit]);

  // Filter units to only non-inverse units for range display
  const availableRangeUnits = units.filter((unit) => !isInverseUnit(unit));

  /**
   * This function is called when user changes the unit of the file. It finds the correct unit object from the
   * units array and sets it as the new unit for the file.
   * @function handleUnitChange
   * @param {Event} e - This is the event that is triggered when user changes the unit of the file.
   */
  const handleUnitChange = (e) => {
    const value = e.target.value;
    const unit = units.filter((u) => u.value === value)[0];
    if (unit) {
      setXUnit(unit);
    }
  };

  // Convert display values back to cm^-1 when updating
  const handleRangeMinChange = (value) => {
    try {
      if (value === "" || value === null || value === undefined) {
        setRangeMin("");
        setDisplayRangeMin("");
        return;
      }
      const numValue = typeof value === "number" ? value : parseFloat(value);
      if (isNaN(numValue)) {
        setRangeMin("");
        setDisplayRangeMin("");
        return;
      }
      // Convert from display unit back to cm^-1
      if (displayRangeUnit.value === units[0].value) {
        setRangeMin(numValue);
        setDisplayRangeMin(numValue);
      } else {
        const cm1Value = anyUnitToCm1(displayRangeUnit, numValue);
        setRangeMin(cm1Value);
        setDisplayRangeMin(numValue);
      }
    } catch (error) {
      recordedErrorLog(
        "Error in handleRangeMinChange: ",
        error,
        true,
        "interface error",
        { value: value }
      );
    }
  };

  const handleRangeMaxChange = (value) => {
    try {
      if (value === "" || value === null || value === undefined) {
        setRangeMax("");
        setDisplayRangeMax("");
        return;
      }
      const numValue = typeof value === "number" ? value : parseFloat(value);
      if (isNaN(numValue)) {
        setRangeMax("");
        setDisplayRangeMax("");
        return;
      }
      // Convert from display unit back to cm^-1
      if (displayRangeUnit.value === units[0].value) {
        setRangeMax(numValue);
        setDisplayRangeMax(numValue);
      } else {
        const cm1Value = anyUnitToCm1(displayRangeUnit, numValue);
        setRangeMax(cm1Value);
        setDisplayRangeMax(numValue);
      }
    } catch (error) {
      recordedErrorLog(
        "Error in handleRangeMaxChange: ",
        error,
        true,
        "interface error",
        { value: value }
      );
    }
  };

  const handleDisplayRangeUnitChange = (e) => {
    try {
      const value = e.target.value;
      const selectedUnit = availableRangeUnits.find((u) => u.value === value);
      if (selectedUnit) {
        // Just change the display unit - the actual rangeMin/rangeMax stay in cm^-1
        // The displayed values will update automatically through getDisplayRangeMin/Max
        setDisplayRangeUnit(selectedUnit);
        if (rangeMin !== "") {
          setDisplayRangeMin(cm1ToAnyUnit(selectedUnit, rangeMin));
        } else {
          setDisplayRangeMin("");
        }
        if (rangeMax !== "") {
          setDisplayRangeMax(cm1ToAnyUnit(selectedUnit, rangeMax));
        } else {
          setDisplayRangeMax("");
        }
      }
    } catch (error) {
      recordedErrorLog(
        "Error in handleDisplayRangeUnitChange: ",
        error
      );
    }
  };

  // This useEffect will run both on mount and when the file in uploadedFiles changes
  useEffect(() => {
    try {
      setXUnit(file.xUnit);
      setRangeMin(file.dataRangeMin);
      setRangeMax(file.dataRangeMax);
      setDisplayRangeMin(cm1ToAnyUnit(displayRangeUnit, file.dataRangeMin));
      setDisplayRangeMax(cm1ToAnyUnit(displayRangeUnit, file.dataRangeMax));
      // Ensure edges have unit property (default to units[0] if missing)
      const edgesWithUnits = (file.edges || []).map((edge) => ({
        ...edge,
        unit: edge.unit || units[0],
      }));
      setEdges(edgesWithUnits);

      // Calculate and update pointsInRange whenever the file changes
      let cutData = filterCurveDataToRange(
        file.dataPoints,
        file.dataRangeMin,
        file.dataRangeMax
      );

      cutData = segmentsIntoSingleCurve(
        splitCurveIntoSegmentsByEdges(cutData, file.edges)
      );

      setPointsInRange(cutData.length);
    } catch (error) {
      recordedErrorLog(
        "Error in useEffect after some values upadte failure: ",
        error,
        true,
        "interface error",
        { file: file }
      );
    }
  }, [uploadedFiles, index]);

  useEffect(() => {
    try {
      let copyOfFile = deepCopy(file);

      let needsUpdate = false;

      if (!isDeepEqual(copyOfFile.xUnit, xUnit)) {
        needsUpdate = true;
        const revertedDataPoints = revertToOriginalPoints(
          copyOfFile.dataPoints,
          copyOfFile.options
        );

        const revertedToOrgUnits = revertDataPointsToOriginalUnits(
          revertedDataPoints,
          copyOfFile.xUnit
        );

        const newOptions = { ...copyOfFile.options, xUnit: xUnit };

        const dataPoints = dataPointsUpdateWithOptions(
          revertedToOrgUnits,
          newOptions,
          recordedErrorLog
        );

        copyOfFile = updateFileDataAfterOptionChange(
          copyOfFile,
          dataPoints,
          newOptions,
          defaultXAxisUnit
        );
      }
      // if (copyOfFile.npoints !== npoints) {
      //   copyOfFile.npoints = npoints;
      //   needsUpdate = true;
      // }

      let cutData = filterCurveDataToRange(
        copyOfFile.dataPoints,
        copyOfFile.dataRangeMin,
        copyOfFile.dataRangeMax
      );

      cutData = segmentsIntoSingleCurve(
        splitCurveIntoSegmentsByEdges(cutData, copyOfFile.edges)
      );

      copyOfFile.poinsInRange = cutData.length;

      if (needsUpdate) {
        setPointsInRange(copyOfFile.poinsInRange);
        setRangeMax(copyOfFile.dataRangeMax);
        setRangeMin(copyOfFile.dataRangeMin);
        setDisplayRangeMin(
          cm1ToAnyUnit(displayRangeUnit, copyOfFile.dataRangeMin)
        );
        setDisplayRangeMax(
          cm1ToAnyUnit(displayRangeUnit, copyOfFile.dataRangeMax)
        );
        setEdges(copyOfFile.edges);
        updateSelected(copyOfFile, index);
      }
    } catch (error) {
      recordedErrorLog("useEffect after some values upadte failure: ", error);
    }
  }, [xUnit]);
  // }, [xUnit, rangeMin, rangeMax, edges, npoints]);

  useEffect(() => {
    try {
      let copyOfFile = deepCopy(file);

      let needsUpdate = false;

      if (copyOfFile.dataRangeMin !== rangeMin) {
        copyOfFile.dataRangeMin = rangeMin;
        needsUpdate = true;
      }
      if (copyOfFile.dataRangeMax !== rangeMax) {
        copyOfFile.dataRangeMax = rangeMax;
        needsUpdate = true;
      }
      if (!isDeepEqual(edges, copyOfFile.edges)) {
        copyOfFile.edges = edges;
        needsUpdate = true;
      }
      // if (copyOfFile.npoints !== npoints) {
      //   copyOfFile.npoints = npoints;
      //   needsUpdate = true;
      // }

      let cutData = filterCurveDataToRange(
        copyOfFile.dataPoints,
        copyOfFile.dataRangeMin,
        copyOfFile.dataRangeMax
      );

      cutData = segmentsIntoSingleCurve(
        splitCurveIntoSegmentsByEdges(cutData, copyOfFile.edges)
      );

      copyOfFile.poinsInRange = cutData.length;

      if (needsUpdate) {
        setPointsInRange(copyOfFile.poinsInRange);
        updateSelected(copyOfFile, index);
      }
    } catch (error) {
      recordedErrorLog("useEffect after some values upadte failure: ", error);
    }
  }, [rangeMin, rangeMax, edges]);

  /**
   * This function updates the min edge point of the edge with the given index.
   * @function updateCutMin
   * @param {string} value - This is a string that represents the value of the min edge point.
   * @param {number} index - This is an index of the edge that we want to update.
   */
  function updateCutMin(value, index) {
    try {
      const cutParamsCopy = deepCopy(edges);
      if (value === "") {
        cutParamsCopy[index] = { ...cutParamsCopy[index], min: value };
      } else {
        cutParamsCopy[index] = {
          ...cutParamsCopy[index],
          min: parseFloat(value),
        };
      }
      setEdges(cutParamsCopy);
    } catch (error) {
      recordedErrorLog("Min cut update failure: ", error);
    }
  }

  /**
   * This function updates the max edge point of the edge with the given index.
   * @function updateCutMax
   * @param {string} value - This is a string that represents the value of the max edge point.
   * @param {number} index - This is an index of the edge that we want to update.
   */
  function updateCutMax(value, index) {
    try {
      const cutParamsCopy = deepCopy(edges);
      if (value === "") {
        cutParamsCopy[index] = { ...cutParamsCopy[index], max: value };
      } else {
        cutParamsCopy[index] = {
          ...cutParamsCopy[index],
          max: parseFloat(value),
        };
      }
      setEdges(cutParamsCopy);
    } catch (error) {
      recordedErrorLog("Max cut update failure: ", error);
    }
  }

  /**
   * This function adds a new empty edge to the list of edges.
   * @function handleAddCut
   */
  function handleAddCut() {
    setEdges([...edges, { min: "", max: "", unit: defaultXAxisUnit }]);
  }

  /**
   * This function updates the unit for an edge without converting min/max values.
   * @function updateEdgeUnit
   * @param {Event} e - The event from the unit selector
   * @param {number} index - The index of the edge to update
   */
  function updateEdgeUnit(e, index) {
    try {
      const value = e.target.value;
      const newUnit = units.find((u) => u.value === value);
      if (!newUnit) return;

      const cutParamsCopy = deepCopy(edges);
      const currentEdge = cutParamsCopy[index];

      // Only update the unit, don't convert min/max values
      cutParamsCopy[index] = {
        ...currentEdge,
        unit: newUnit,
      };
      setEdges(cutParamsCopy);
    } catch (error) {
      recordedErrorLog("Edge unit update failure: ", error);
    }
  }

  /**
   * This function deletes the edge with the given index.
   * @function handleDeleteCut
   * @param {number} index - This is an index of the edge that we want to delete.
   */
  function handleDeleteCut(index) {
    setEdges(edges.filter((_, i) => i !== index));
  }

  // /**
  //  * This function updates the number of points of the file.
  //  * @function handleNPointsChange
  //  * @param {Event} e - This is the event that is triggered when user changes the number of points of the file.
  //  */
  // function handleNPointsChange(e) {
  //   if (e.target.value > 0) {
  //     setNpoints(parseInt(e.target.value));
  //   }
  // }

  /**
   * This function resets the data range to the min and max of the actual data points.
   * @function handleResetRange
   */
  function handleResetRange() {
    try {
      if (file.dataPoints && file.dataPoints.length > 0) {
        // Calculate exact min and max values without rounding
        const maxX = file.dataPoints.reduce(function (max, p) {
          return p.x > max ? p.x : max;
        }, file.dataPoints[0].x);

        const minX = file.dataPoints.reduce(function (min, p) {
          return p.x < min ? p.x : min;
        }, file.dataPoints[0].x);
        setRangeMin(minX);
        setRangeMax(maxX);
        setDisplayRangeMin(cm1ToAnyUnit(displayRangeUnit, minX));
        setDisplayRangeMax(cm1ToAnyUnit(displayRangeUnit, maxX));
      }
    } catch (error) {
      recordedErrorLog(
        "Error in handleResetRange: ",
        error,
        true,
        "interface error",
        { file: file }
      );
    }
  }

  /**
   * This function sets file that we want to edit again in file options window.
   * @function handleNameClick
   */
  function handleNameClick() {
    setFileToChange(file.ID);
  }

  return (
    <div
      className="stickyFileWindow"
      style={edges.length > 1 ? { height: "450px" } : {}}
      id="stickyFileEditWindow"
    >
      <div className="header">
        <div
          className="fileName"
          onClick={() => handleNameClick()}
          id="sticky-file-window-file-name"
        >
          {file.name.length > 28 ? (
            <HtmlTooltip
              title={
                <React.Fragment>
                  <div className="customTooltip">{file.name}</div>
                </React.Fragment>
              }
            >
              <span>{getShortName(file.name, 28)}</span>
            </HtmlTooltip>
          ) : (
            file.name
          )}
        </div>
        {callFromGraph ? (
          <div className="closeButtonContainer">
            <CloseButton
              onClose={resetSelection}
              customId="close-sticky-file-window"
            />
          </div>
        ) : (
          // <img
          //   src={close}
          //   alt="Close Button"
          //   className="closeIcon"
          //   id="close-sticky-file-window"
          //   onClick={() => resetSelection()}
          // />
          <></>
        )}
      </div>
      <hr />
      <div className="unitsContainer">
        <div className="unitSelectorLabel">
          <div className="title">x-data unit in original file:</div>
        </div>
        <div className="xUnitSelectContainer">
          <Select
            MenuProps={{
              container: () => document.getElementById("stickyFileEditWindow"),
            }}
            labelId="selectUnit"
            id="unitSelection"
            value={xUnit.value}
            onChange={(e) => handleUnitChange(e)}
            className="x_unit_select"
            style={{ paddingTop: "0px" }}
          >
            {units.map(({ name, value }) => (
              <MenuItem key={name} value={value}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </div>
      </div>
      {/* <div className="nPoints">
        <div className="title">Npoints:</div>
        <TextField
          style={{
            height: "100%",
          }}
          value={npoints}
          onChange={handleNPointsChange}
          className="points"
          size="small"
          type="number"
        />
         <div className="points">{file.npoints}</div>
      </div> */}
      <div className="nPoints">
        <div className="title">Selected Data Points:</div>
        <div>{pointsInRange}</div>
      </div>
      <div style={{ marginLeft: "5px", marginTop: "5px" }}>
        <div
          className="rangeTitle"
          title="Set the outer limits of the data range in which you want to fit and display your data. If you want to exclude the range in different units than are available here, you can do so by adding exclusion ranges below."
        >
          Data Range:
          <HelpOutlineIcon className="infoIcon" />
        </div>
        <div
          className="rangeWithUnitSelector"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginTop: "3px",
          }}
        >
          <Range
            initialMin={displayRangeMin}
            initialMax={displayRangeMax}
            updateMin={handleRangeMinChange}
            updateMax={handleRangeMaxChange}
            rangeTitle={null}
          />
          <FormControl
            variant="standard"
            size="small"
            style={{ minWidth: "100px" }}
          >
            <Select
              MenuProps={{
                container: () =>
                  document.getElementById("stickyFileEditWindow"),
                PaperProps: {
                  style: {
                    zIndex: 10000,
                  },
                },
              }}
              value={displayRangeUnit.value}
              onChange={handleDisplayRangeUnitChange}
              className="display_range_unit_select"
              renderValue={(value) => {
                const selectedUnit = availableRangeUnits.find(
                  (u) => u.value === value
                );
                if (!selectedUnit) return value;
                return selectedUnit.name;
              }}
            >
              {availableRangeUnits.map((unit) => (
                <MenuItem key={unit.value} value={unit.value}>
                  {unit.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>
      <div className="resetRangeButtonContainer">
        <CustomButton
          text="Reset Range"
          handleClick={handleResetRange}
          id="reset-range-button"
        />
      </div>
      <div
        className="cutSettingsTitle"
        id="exclusion-ranges-title"
        title="Add data ranges that you want to exclude from your fitting and displaying."
      >
        Add Exclusion Ranges:
        <HelpOutlineIcon className="infoIcon" />
      </div>
      <div
        className="allCutsContainer"
        style={edges.length > 1 ? {} : { height: "75px" }}
      >
        {edges &&
          edges.map((param, index) => {
            const edgeUnit = param.unit || units[0];
            return (
              <div className="cutContainer" key={index}>
                <div
                  className="rangeWithUnitSelector"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginTop: "3px",
                    marginLeft: "5px",
                  }}
                >
                  <Range
                    initialMin={param.min}
                    initialMax={param.max}
                    updateMin={updateCutMin}
                    updateMax={updateCutMax}
                    rangeTitle={null}
                    index={index}
                  />
                  <FormControl
                    variant="standard"
                    size="small"
                    style={{ minWidth: "100px" }}
                  >
                    <Select
                      MenuProps={{
                        container: () =>
                          document.getElementById("stickyFileEditWindow"),
                        PaperProps: {
                          style: {
                            zIndex: 10000,
                          },
                        },
                      }}
                      value={edgeUnit.value}
                      onChange={(e) => updateEdgeUnit(e, index)}
                      className="edge_unit_select"
                      renderValue={(value) => {
                        const selectedUnit = units.find(
                          (u) => u.value === value
                        );
                        if (!selectedUnit) return value;
                        return selectedUnit.name;
                      }}
                    >
                      {units.map((unit) => (
                        <MenuItem key={unit.value} value={unit.value}>
                          {unit.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                <img
                  src={deleteIcon}
                  alt="delete Cut"
                  className="deleteCut"
                  onClick={() => handleDeleteCut(index)}
                />
              </div>
            );
          })}
        <img
          src={addIcon}
          alt="add another Cut"
          className="addCut"
          onClick={() => handleAddCut()}
        />
      </div>
    </div>
  );
}

export default StickyFileWindow;
